import React, { useState, useEffect } from "react";
import { Modal, Form, Tabs, Input, Select, InputNumber, Radio, Upload, Button, Space, Tag, message } from "antd";
import { CloudUploadOutlined, DeleteOutlined } from "@ant-design/icons";
import { MenuItem } from "../RestaurantPOS";
import { KitchenGroup } from "./KitchenGroupFormModal";
import KitchenGroupSelect from "./KitchenGroupSelect";

interface MenuCategory {
  id: string;
  name: string;
}

interface MenuItemFormModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: any, selectedImage: File | null) => Promise<void>;
  editingItem: MenuItem | null;
  categories: MenuCategory[];
  kitchenGroups: KitchenGroup[];
  loading: boolean;
}

const MenuItemFormModal: React.FC<MenuItemFormModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  editingItem,
  categories,
  kitchenGroups,
  loading,
}) => {
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState("basic");
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imageInputMode, setImageInputMode] = useState<"upload" | "url">("upload");
  const [badgeInputValue, setBadgeInputValue] = useState("");

  const itemNameValue = Form.useWatch("name", form);
  const priceTakeAwayValue = Form.useWatch("price_take_away", form);
  const priceDineInValue = Form.useWatch("price_dine_in", form);
  const imageUrlValue = Form.useWatch("image_url", form);

  const isSubmitDisabled = !itemNameValue || priceTakeAwayValue === undefined || priceDineInValue === undefined;

  useEffect(() => {
    if (visible) {
      if (editingItem) {
        form.setFieldsValue(editingItem);
        setImageInputMode(editingItem.image_url ? "url" : "upload");
      } else {
        form.resetFields();
        if (categories.length > 0) {
          form.setFieldsValue({ category_id: categories[0].id, is_available: true, is_active: true, display_order: 0 });
        }
        setImageInputMode("upload");
      }
      setActiveTab("basic");
      setSelectedImage(null);
      setBadgeInputValue("");
    }
  }, [visible, editingItem, categories, form]);

  const handleImageModeChange = (e: any) => {
    const nextMode = e.target.value;
    if (imageInputMode === "upload" && nextMode === "url" && selectedImage) {
      message.warning("Please remove the uploaded file first before providing a URL.");
      return;
    }
    if (imageInputMode === "url" && nextMode === "upload" && imageUrlValue) {
      message.warning("Please clear the image URL first before uploading a file.");
      return;
    }
    setImageInputMode(nextMode);
  };

  const handleAddBadge = () => {
    if (badgeInputValue.trim()) {
      const currentBadgesStr = form.getFieldValue("badge") || "";
      const currentBadges = currentBadgesStr.split(",").map((b: string) => b.trim()).filter(Boolean);
      const newBadge = badgeInputValue.trim().toUpperCase();
      if (!currentBadges.includes(newBadge)) {
        form.setFieldValue("badge", [...currentBadges, newBadge].join(", "));
      }
      setBadgeInputValue("");
    }
  };

  const handleRemoveBadge = (badgeToRemove: string) => {
    const currentBadgesStr = form.getFieldValue("badge") || "";
    const currentBadges = currentBadgesStr.split(",").map((b: string) => b.trim()).filter(Boolean);
    form.setFieldValue("badge", currentBadges.filter((b: string) => b !== badgeToRemove).join(", "));
  };

  const onFinish = async (values: any) => {
    await onSubmit(values, selectedImage);
  };

  return (
    <Modal
      title={editingItem ? "Edit Menu Item" : "Add Menu Item"}
      open={visible}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      width={640}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key)}
          items={[
            {
              key: "basic",
              label: "Basic Info",
              children: (
                <div style={{ paddingTop: 16 }}>
                  <Form.Item
                    name="name"
                    label="Item Name"
                    rules={[{ required: true, message: "Item name is required" }]}
                  >
                    <Input placeholder="e.g. Grilled Lobster" />
                  </Form.Item>
                  <Form.Item name="category_id" label="Category">
                    <Radio.Group
                      optionType="button"
                      buttonStyle="solid"
                      style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                    >
                      {categories.map((cat) => (
                        <Radio.Button
                          key={cat.id}
                          value={cat.id}
                          onClick={(e) => {
                            if (form.getFieldValue("category_id") === cat.id) {
                              e.preventDefault();
                              form.setFieldValue("category_id", undefined);
                            }
                          }}
                          style={{ borderRadius: "6px", borderInlineStart: "1px solid #d9d9d9", userSelect: "none" }}
                        >
                          {cat.name}
                        </Radio.Button>
                      ))}
                    </Radio.Group>
                  </Form.Item>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <Form.Item
                      name="price_take_away"
                      label="Take Away Price"
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <InputNumber min={0} step={0.01} style={{ width: "100%" }} placeholder="0.00" prefix="LKR" />
                    </Form.Item>
                    <Form.Item
                      name="price_dine_in"
                      label="Dine-In / Room Service Price"
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <InputNumber min={0} step={0.01} style={{ width: "100%" }} placeholder="0.00" prefix="LKR" />
                    </Form.Item>
                  </div>
                  <Form.Item name="kitchen_printer_group_id" label="Kitchen Printer Group">
                    <KitchenGroupSelect groups={kitchenGroups} />
                  </Form.Item>
                  <Form.Item name="description" label="Description">
                    <Input.TextArea rows={3} placeholder="Detailed description of the meal..." />
                  </Form.Item>
                </div>
              ),
            },
            {
              key: "media",
              label: "Media & Display",
              children: (
                <div style={{ paddingTop: 16 }}>
                  <Form.Item label="Image Source" style={{ marginBottom: 16 }}>
                    <Radio.Group value={imageInputMode} onChange={handleImageModeChange} optionType="button" buttonStyle="solid" style={{ width: "100%", display: "flex" }}>
                      <Radio.Button value="upload" style={{ flex: 1, textAlign: "center" }}>Upload File</Radio.Button>
                      <Radio.Button value="url" style={{ flex: 1, textAlign: "center" }}>Image URL</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                  {imageInputMode === "upload" ? (
                    <Form.Item name="image_url" label="Menu Image">
                      <Upload
                        listType="picture-card"
                        maxCount={1}
                        beforeUpload={(file) => {
                          setSelectedImage(file);
                          return false;
                        }}
                        onRemove={() => {
                          setSelectedImage(null);
                          form.setFieldValue("image_url", undefined);
                        }}
                        fileList={selectedImage ? [{ uid: "-1", name: selectedImage.name, status: "done" }] : []}
                      >
                        {!selectedImage && (
                          <div>
                            <CloudUploadOutlined />
                            <div style={{ marginTop: 8 }}>Upload</div>
                          </div>
                        )}
                      </Upload>
                    </Form.Item>
                  ) : (
                    <Form.Item name="image_url" label="Image URL">
                      <Input placeholder="https://example.com/image.jpg" />
                    </Form.Item>
                  )}
                  <Form.Item label="Badges">
                    <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                      {form.getFieldValue("badge")?.split(",").map((b: string) => b.trim()).filter(Boolean).map((badge: string, i: number) => (
                        <Tag key={i} closable onClose={() => handleRemoveBadge(badge)} color="purple">{badge}</Tag>
                      ))}
                    </div>
                    <Space.Compact style={{ width: "100%" }}>
                      <Input
                        value={badgeInputValue}
                        onChange={(e) => setBadgeInputValue(e.target.value)}
                        placeholder="e.g. CHEF'S SPECIAL"
                        onPressEnter={handleAddBadge}
                      />
                      <Button onClick={handleAddBadge}>Add</Button>
                    </Space.Compact>
                  </Form.Item>
                </div>
              ),
            },
            {
              key: "settings",
              label: "Settings",
              children: (
                <div style={{ paddingTop: 16 }}>
                  <Form.Item name="is_available" label="Available for Ordering" initialValue={true}>
                    <Radio.Group optionType="button" buttonStyle="solid" style={{ width: "100%", display: "flex" }}>
                      <Radio.Button value={true} style={{ flex: 1, textAlign: "center" }}>Available</Radio.Button>
                      <Radio.Button value={false} style={{ flex: 1, textAlign: "center" }}>Unavailable</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item name="is_active" label="Active (visible in system)" initialValue={true}>
                    <Radio.Group optionType="button" buttonStyle="solid" style={{ width: "100%", display: "flex" }}>
                      <Radio.Button value={true} style={{ flex: 1, textAlign: "center" }}>Active</Radio.Button>
                      <Radio.Button value={false} style={{ flex: 1, textAlign: "center" }}>Inactive</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item name="is_featured" label="Featured on Website" initialValue={false}>
                    <Radio.Group optionType="button" buttonStyle="solid" style={{ width: "100%", display: "flex" }}>
                      <Radio.Button value={true} style={{ flex: 1, textAlign: "center" }}>Yes</Radio.Button>
                      <Radio.Button value={false} style={{ flex: 1, textAlign: "center" }}>No</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item name="display_order" label="Display Order" initialValue={0}>
                    <InputNumber min={0} style={{ width: "100%" }} />
                  </Form.Item>
                </div>
              ),
            },
          ]}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={loading} disabled={isSubmitDisabled}>
            {editingItem ? "Update" : "Create"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default MenuItemFormModal;
